import React, { useCallback, useEffect, useReducer, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from 'reactstrap';
import QuarterHourTimeSelect from '../../../common/QuarterHourTimeSelect';
import Select from 'react-select';
import {
  formatStaffDoctorOptionLabel,
  mergeStaffDoctorSelectStyles,
} from '../../../../utils/staffDoctorSelect';

import Error from '../../../../shared/components/form/Error';
import DatePickerComponent from '../../Common/DatePickerComponent';
import { createAppointment } from '../../../../redux/actions/appointmentAction';
import AppointmentTypesSelect from '../../../common/AppointmentTypesSelect';

import {
  useAppointmentRequestModalContext,
  useSuccessModalContext,
} from '../utils';
import { useAppointmentLabels } from '../../../App/customize/hooks/useAppointmentLabels';

const DUPLICATE_APPOINTMENT_SAME_DAY = 'DUPLICATE_APPOINTMENT_SAME_DAY';

function isDuplicateAppointmentSameDayError(error) {
  const text = [
    error?.data?.errorMessage,
    error?.data?.error,
    error?.errorMessage,
    error?.message,
    error?.response?.data?.errorMessage,
    error?.response?.data?.error,
  ]
    .filter(Boolean)
    .join(' ');
  return text.includes(DUPLICATE_APPOINTMENT_SAME_DAY);
}

function duplicateAppointmentSameDayMessage(petName) {
  const name = String(petName || '').trim() || 'This pet';
  return `${name} already has an appointment at your facility on this day.`;
}

// Create Appointment Block
const initialAppointmentState = {
  appointmentDate: '',
  time: '',
  doctor: '',
  parentId: '',
  pet: '',
  type: '',
};

function appointmentDetailsReducer(state, action) {
  switch (action.type) {
    case 'SET_APPOINTMENT_DATE':
      return { ...state, appointmentDate: action.payload };
    case 'SET_TIME':
      return { ...state, time: action.payload };
    case 'SET_TYPE':
      return {
        ...state,
        type: action.payload,
      };
    case 'SET_DOCTOR':
      return { ...state, doctor: action.payload };
    case 'SET_APPOINTMENT_REQUEST_DATA':
      return {
        ...state,
        doctor: action.payload.doctorId || null,
        parentId: action.payload.parentId || '',
        pet: action.payload.petId || '',
        type: action.payload.type || '',
      };
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
}

const FieldWrapper = ({
  label,
  gridArea,
  isRequired = false,
  error,
  children,
}) => (
  <div className="modal-create-appointment-field" style={{ gridArea }}>
    <p className="field-label">
      {label}
      {isRequired && <span className="required">*</span>}
    </p>
    {children}
    {error && <Error text={error} />}
  </div>
);

export function CreateAppointmentBlock({
  appointmentRequestData,
  doctors,
  defaultDoctor,
  defaultAppointmentType,
  onScrollToMessageHistory,
}) {
  const dispatch = useDispatch();
  const [appointmentDate, setAppointmentDate] = useState(null);
  const [appointmentTime, setAppointmentTime] = useState(null);
  const [appointmentType, setAppointmentType] = useState(
    defaultAppointmentType[0].value,
  );
  const [selectedDoctor, setSelectedDoctor] = useState(defaultDoctor);
  const [errors, setErrors] = useState({});
  const { closeAppointmentRequestModal } = useAppointmentRequestModalContext();
  const { temporaryShowSuccessModal } = useSuccessModalContext();
  const labels = useAppointmentLabels();

  const [appointmentDetails, dispatchAppointmentDetails] = useReducer(
    appointmentDetailsReducer,
    initialAppointmentState,
  );

  useEffect(() => {
    if (appointmentRequestData) {
      dispatchAppointmentDetails({
        type: 'SET_APPOINTMENT_REQUEST_DATA',
        payload: appointmentRequestData,
      });
    }
  }, [appointmentRequestData]);

  const handleSetAppointmentDetails = useCallback((type, value) => {
    dispatchAppointmentDetails({ type, payload: value });
  }, []);

  const handleDateChange = (date) => {
    setAppointmentDate(date);
    setErrors((prevErrors) => ({ ...prevErrors, appointmentDate: '' }));
    handleSetAppointmentDetails('SET_APPOINTMENT_DATE', date);
  };

  const handleTimeChange = (time) => {
    setAppointmentTime(time);
    setErrors((prevErrors) => ({ ...prevErrors, appointmentTime: '' }));
    handleSetAppointmentDetails('SET_TIME', time.format('hh:mm A'));
  };

  const handleTypeChange = (e) => {
    setAppointmentType(e.target.value);
    handleSetAppointmentDetails('SET_TYPE', e.target.value);
  };

  const handleDoctorChange = (doctorData) => {
    setSelectedDoctor([doctorData]);
    handleSetAppointmentDetails(
      'SET_DOCTOR',
      doctorData.value ? doctorData.value : null,
    );
  };

  const handleSubmit = async () => {
    const newErrors = {};

    if (!appointmentDate) {
      newErrors.appointmentDate = 'Date is a required field';
    }
    if (!appointmentTime) {
      newErrors.appointmentTime = 'Time is a required field';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

try {
  await dispatch(
    createAppointment({
      ...appointmentDetails,
      appointmentRequestId: appointmentRequestData?.id,
    }),
  );
  closeAppointmentRequestModal();
  temporaryShowSuccessModal();
} catch (error) {
  if (isDuplicateAppointmentSameDayError(error)) {
    setErrors({
      appointmentDate: duplicateAppointmentSameDayMessage(
        appointmentRequestData?.petName,
      ),
    });
    return;
  }
  throw error;
}
  };

  return (
    <div className="modal-create-appointment-wrapper">
      <div className="modal-create-appointment-block">
        <FieldWrapper
          label={labels.appointmentDate}
          gridArea="date"
          isRequired={true}
          error={errors.appointmentDate}
        >
          <DatePickerComponent
            minDate
            maxDate={false}
            viewMode={false}
            date={appointmentDate}
            handleChange={handleDateChange}
            portalMenu
            compact
          />
        </FieldWrapper>
        <FieldWrapper
          label={labels.appointmentTime}
          gridArea="time"
          isRequired={true}
          error={errors.appointmentTime}
        >
<QuarterHourTimeSelect
  value={appointmentTime}
  onChange={handleTimeChange}
  placeholder="HH:MM AM"
  name="appointmentTime"
/>
        </FieldWrapper>
        <FieldWrapper label="Doctor" gridArea="doctor">
          <Select
            value={doctors.filter(
              (option) => option.value === selectedDoctor[0]?.value,
            )}
            onChange={handleDoctorChange}
            name="selectedDoctor"
            matchProp="_id"
            disabled={false}
            classNamePrefix="doctors-select"
            className="custom-select-control doctors-select"
            options={doctors}
            formatOptionLabel={formatStaffDoctorOptionLabel}
            placeholder="Select Doctor"
            styles={mergeStaffDoctorSelectStyles()}
          />
        </FieldWrapper>
        <FieldWrapper label={labels.appointmentType} gridArea="type">
          <AppointmentTypesSelect
            handleChange={handleTypeChange}
            disabled={false}
            value={appointmentType}
          />
        </FieldWrapper>
        <Button
          className="save-update"
          style={{ gridArea: 'button' }}
          color="primary"
          onClick={handleSubmit}
        >
          {labels.createAppointment}
        </Button>
        <div className="msg-history-link-cell" style={{ gridArea: 'msg-history-link' }}>
          <button
            type="button"
            className="nav-link-style"
            onClick={onScrollToMessageHistory}
          >
            Message History
          </button>
        </div>
      </div>
    </div>
  );
}
